/**
 * Enhanced cache with persistent localStorage, request deduplication, and TTL.
 * - In-memory cache for fast access
 * - localStorage persistence for page refreshes
 * - Request deduplication to prevent concurrent duplicate requests
 * - Stale-while-revalidate pattern for instant responses
 */

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_PREFIX = 'convpilot_cache_';
const MAX_STORAGE_SIZE = 50; // Max items in localStorage

/** Active data-scope segment (e.g. none, c:TEST, c:TEST+shared, admin). */
let cacheScopeSeg = 'none';

/**
 * Set the client data scope used to namespace dashboard cache keys.
 * Call on login /auth/me so private clients do not reuse shared cache entries.
 */
export function setCacheScope(scope: {
  role?: string | null;
  clientCode?: string | null;
  includeSharedData?: boolean | null;
} | null): void {
  const role = (scope?.role || '').toLowerCase();
  if (role === 'admin') {
    cacheScopeSeg = 'admin';
  } else if (scope?.clientCode) {
    cacheScopeSeg = scope.includeSharedData === true
      ? `c:${scope.clientCode}+shared`
      : `c:${scope.clientCode}`;
  } else {
    cacheScopeSeg = 'none';
  }
}

function scopedKey(key: string): string {
  return `${cacheScopeSeg}::${key}`;
}

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
  storedAt: number;
}

// In-memory cache for fast access
const memoryStore = new Map<string, CacheEntry<unknown>>();

// Request deduplication: track in-flight requests
const pendingRequests = new Map<string, Promise<unknown>>();

// ============================================
// localStorage Helpers
// ============================================

/**
 * Get item from localStorage with JSON parsing
 */
function getFromStorage<T>(key: string): CacheEntry<T> | null {
  try {
    const item = localStorage.getItem(STORAGE_PREFIX + key);
    if (!item) return null;
    return JSON.parse(item) as CacheEntry<T>;
  } catch {
    return null;
  }
}

/**
 * Set item in localStorage with JSON stringify
 */
function setInStorage<T>(key: string, entry: CacheEntry<T>): void {
  try {
    // Check storage size and evict old entries if needed
    pruneStorageIfNeeded();
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable, ignore
  }
}

/**
 * Remove old entries from localStorage to prevent quota issues
 */
function pruneStorageIfNeeded(): void {
  try {
    const keys: { key: string; storedAt: number }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (fullKey?.startsWith(STORAGE_PREFIX)) {
        const entry = getFromStorage(fullKey.replace(STORAGE_PREFIX, ''));
        if (entry) {
          keys.push({ key: fullKey.replace(STORAGE_PREFIX, ''), storedAt: entry.storedAt });
        }
      }
    }
    
    // If over limit, remove oldest entries
    if (keys.length >= MAX_STORAGE_SIZE) {
      keys.sort((a, b) => a.storedAt - b.storedAt);
      const toRemove = keys.slice(0, Math.floor(MAX_STORAGE_SIZE / 4));
      toRemove.forEach(({ key }) => {
        localStorage.removeItem(STORAGE_PREFIX + key);
      });
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Remove item from localStorage
 */
function removeFromStorage(key: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    // Ignore
  }
}

// ============================================
// Core Cache Functions
// ============================================

/**
 * Get cached value if present and not expired. Checks memory first, then localStorage.
 */
export function get<T>(key: string): T | null {
  const sk = scopedKey(key);
  // Check memory cache first (fastest)
  const memEntry = memoryStore.get(sk) as CacheEntry<T> | undefined;
  if (memEntry) {
    if (Date.now() > memEntry.expiresAt) {
      memoryStore.delete(sk);
      removeFromStorage(sk);
      return null;
    }
    return memEntry.data;
  }

  // Check localStorage (persists across page loads)
  const storageEntry = getFromStorage<T>(sk);
  if (storageEntry) {
    if (Date.now() > storageEntry.expiresAt) {
      removeFromStorage(sk);
      return null;
    }
    // Promote to memory cache for faster subsequent access
    memoryStore.set(sk, storageEntry);
    return storageEntry.data;
  }

  return null;
}

/**
 * Store value in both memory and localStorage with TTL.
 */
export function set<T>(key: string, data: T, ttlMs: number = DEFAULT_TTL_MS): void {
  const sk = scopedKey(key);
  const entry: CacheEntry<T> = {
    data,
    expiresAt: Date.now() + ttlMs,
    storedAt: Date.now(),
  };
  
  memoryStore.set(sk, entry as CacheEntry<unknown>);
  setInStorage(sk, entry);
}

/**
 * Remove a key from both memory and localStorage.
 */
export function invalidate(key: string): void {
  const sk = scopedKey(key);
  memoryStore.delete(sk);
  removeFromStorage(sk);
}

/**
 * Invalidate all keys that start with the given prefix.
 */
export function invalidatePrefix(prefix: string): void {
  // Clear from memory
  for (const key of memoryStore.keys()) {
    if (key.startsWith(prefix)) memoryStore.delete(key);
  }
  
  // Clear from localStorage
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (fullKey?.startsWith(STORAGE_PREFIX + prefix)) {
        keysToRemove.push(fullKey);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore
  }
}

/**
 * Clear all cache entries
 */
export function clearAll(): void {
  memoryStore.clear();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (fullKey?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(fullKey);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Ignore
  }
}

/**
 * Fetch data with request deduplication.
 * If same key is already being fetched, waits for that request instead of making a new one.
 */
export function getOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const sk = scopedKey(key);
  // Check cache first
  const cached = get<T>(key);
  if (cached != null) return Promise.resolve(cached);

  // Check if request is already in flight (deduplication)
  const pending = pendingRequests.get(sk);
  if (pending) {
    return pending as Promise<T>;
  }

  // Make new request
  const request = fetcher()
    .then((data) => {
      set(key, data, ttlMs);
      return data;
    })
    .finally(() => {
      pendingRequests.delete(sk);
    });

  pendingRequests.set(sk, request);
  return request;
}

/**
 * Stale-while-revalidate pattern:
 * - Returns cached data immediately if available (even if expired)
 * - Refreshes cache in background
 * - Calls onBackgroundUpdate when fresh data arrives
 * - Includes request deduplication
 */
export function getCachedOrFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  onBackgroundUpdate?: (data: T) => void,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const sk = scopedKey(key);
  const cached = get<T>(key);
  
  if (cached != null) {
    // Return cached immediately, refresh in background
    if (onBackgroundUpdate) {
      // Check if already fetching in background
      if (!pendingRequests.has(sk)) {
        const bgRequest = fetcher()
          .then((data) => {
            set(key, data, ttlMs);
            onBackgroundUpdate(data);
            return data;
          })
          .finally(() => {
            pendingRequests.delete(sk);
          });
        pendingRequests.set(sk, bgRequest);
      }
    }
    return Promise.resolve(cached);
  }

  // No cache, need to fetch (with deduplication)
  const pending = pendingRequests.get(sk);
  if (pending) {
    return pending as Promise<T>;
  }

  const request = fetcher()
    .then((data) => {
      set(key, data, ttlMs);
      return data;
    })
    .finally(() => {
      pendingRequests.delete(sk);
    });

  pendingRequests.set(sk, request);
  return request;
}

/**
 * Preload cache entries for routes the user is likely to visit.
 * Call this on initial page load to warm the cache.
 */
export function preloadCache(entries: Array<{ key: string; fetcher: () => Promise<unknown>; ttlMs?: number }>): void {
  entries.forEach(({ key, fetcher, ttlMs }) => {
    // Only preload if not already cached
    if (!get(key) && !pendingRequests.has(key)) {
      getOrFetch(key, fetcher, ttlMs).catch(() => {
        // Ignore preload errors
      });
    }
  });
}
